// Analytics Event Model public API
import { type AppearanceModeChangedEventProps } from '@/models/analytics/features/appearance';
import { type AssistantPanelActionEventProps } from '@/models/analytics/features/assistant-view';
import {
  type AudioFailedEventProps,
  type AudioStoppedEventProps,
  type AudioUploadedEventProps,
  type AudioWarningEventProps,
  type RecordingStopFailedEventProps,
} from '@/models/analytics/features/audio';
import { type ForcedLogoutEventProps } from '@/models/analytics/features/auth';
import {
  type BookDemoCliniciansSelectedEventProps,
  type BookDemoCountrySelectedEventProps,
  type BookDemoNextClickedEventProps,
} from '@/models/analytics/features/book-demo';
import { type CalculatorExecutedEventProps } from '@/models/analytics/features/calculators';
import {
  type ChatCreatedEventProps,
  type ChatDeletedEventProps,
  type ChatViewedEventProps,
  type EvidenceClickedEventProps,
  type FeatureToggledEventProps,
  type MessageAbortedEventProps,
  type MessageFailedEventProps,
  type MessageReceivedEventProps,
  type MessageStoppedEventProps,
  type MessageSubmittedEventProps,
  type PanelEventProps,
  type ToggleUpdatedEventProps,
} from '@/models/analytics/features/chat';
import {
  type ChatContextAddFileClickedEventProps,
  type ChatContextAddSessionClickedEventProps,
  type ChatContextAttachPatientClickedEventProps,
  type ChatMessageSentWithContextEventProps,
} from '@/models/analytics/features/chat-context';
import {
  type ClinicalCodeClickableEventProps,
  type ClinicalCodeCopiedEventProps,
} from '@/models/analytics/features/clinical-codes';
import {
  type CommsDashboardTabViewedEventProps,
  type CommsEntryPointClickedEventProps,
  type CommsSetupStartedEventProps,
  type CommsSetupStepAbandonedEventProps,
  type InboxFilterAppliedEventProps,
  type InboxItemUpdatedEventProps,
  type InboxItemViewedEventProps,
  type InboxSessionEndedEventProps,
  type InboxSessionStartedEventProps,
  type KbApplyButtonClickedEventProps,
  type KbApplySuccessViewedEventProps,
  type KbObjectivesTabViewedEventProps,
  type KbPreviewButtonClickedEventProps,
  type KbScanButtonClickedEventProps,
  type ObjectiveRemovedEventProps,
  type ObjectiveSelectedEventProps,
  type ObjectiveViewedEventProps,
} from '@/models/analytics/features/comms';
import {
  type CommunityCreatedEventProps,
  type CommunitySearchedEventProps,
  type CommunityViewedEventProps,
} from '@/models/analytics/features/community';
import { type ComplianceEventProps } from '@/models/analytics/features/compliance';
import type {
  ContactSalesFormOpenedEventProps,
  ContactSalesFormSubmittedEventProps,
} from '@/models/analytics/features/contact-sales';
import {
  type ContextDeletedEventProps,
  type ContextFailedEventProps,
  type ContextFileDeletedEventProps,
  type ContextFileUploadedEventProps,
  type ContextFileUploadFailedEventProps,
  type ContextFileUploadStartedEventProps,
  type ContextUpdatedEventProps,
  type ContextUploadedEventProps,
} from '@/models/analytics/features/context-files';
import {
  type CPDDraftCreatedEventProps,
  type CPDDraftDeletedEventProps,
  type CPDDraftResumedEventProps,
  type CPDExportedEventProps,
  type CPDPageViewedEventProps,
  type FollowUpQuestionClickedEventProps,
} from '@/models/analytics/features/cpd';
import { type DeeplinkTriggeredEventProps } from '@/models/analytics/features/deeplink';
import {
  type DiarisedTranscriptViewedEventProps,
  type SpeakerAttributionUpdatedEventProps,
} from '@/models/analytics/features/diarisation';
import {
  type DictationCompletedEventProps,
  type DictationCsatDismissedEventProps,
  type DictationCsatSubmittedEventProps,
  type DictationErroredEventProps,
  type DictationStartedEventProps,
  type LegacyInlineDictationCompletedEventProps,
} from '@/models/analytics/features/dictation';
import {
  type DictateTourBannerCtaClickedEventProps,
  type DictationLogsSpotlightDismissedEventProps,
} from '@/models/analytics/features/dictation-logs';
import {
  type DocumentSuggestionBannerCarouselNavigatedEventProps,
  type DocumentSuggestionBannerCtaClickedEventProps,
  type DocumentSuggestionBannerDismissedEventProps,
  type DocumentSuggestionBannerShownEventProps,
  type DocumentSuggestionBannerSuppressedEventProps,
} from '@/models/analytics/features/document-suggestion-banner';
import {
  type DocumentCopiedEventProps,
  type DocumentDeletedEventProps,
  type DocumentEmailedEventProps,
  type DocumentEventProps,
  type DocumentExportedEventProps,
  type DocumentSharedEventProps,
  type DocumentSyncedEventProps,
  type DocumentTemplateQueueLimitReachedEventProps,
  type DocumentTemplateQueueReorderedEventProps,
  type DocumentUpdatedEventProps,
  type DocumentViewedEventProps,
  type FormFillingDownloadedEventProps,
  type QueuedDocumentGenerationCancelledEventProps,
  type QueuedDocumentGenerationFailedEventProps,
  type QueuedDocumentGenerationStartedEventProps,
  type QueuedDocumentTabTemplateSwappedEventProps,
  type SessionStartedWithDocumentQueueEventProps,
} from '@/models/analytics/features/documents';
import {
  type VerificationAutoModeToggledEventProps,
  type VerificationAutoPopoverActionEventProps,
  type VerificationAutoPopoverShownEventProps,
  type VerificationAutoTriggeredEventProps,
  type VerificationCompletedEventProps,
  type VerificationFailedEventProps,
  type VerificationFeedbackSubmittedEventProps,
  type VerificationOverallFeedbackSubmittedEventProps,
  type VerificationStartedEventProps,
  type VerificationSuggestedFixAppliedEventProps,
} from '@/models/analytics/features/entity-verification';
import {
  type ErrorViewedEventProps,
  type OnboardingErrorViewedEventProps,
} from '@/models/analytics/features/errors';
import {
  type CreditsConsumedEventProps,
  type EvidenceCategoryClickedEventProps,
  type EvidenceChatAbortedEventProps,
  type EvidenceChatCompletedEventProps,
  type EvidenceChatCtaClickedEventProps,
  type EvidenceChatErrorEventProps,
  type EvidenceChatSubmittedEventProps,
  type EvidenceCheckOtherSourcesClickedEventProps,
  type EvidenceContactSalesClickedEventProps,
  type EvidenceCopiedEventProps,
  type EvidenceCopyConfirmationEventProps,
  type EvidenceExperienceFeedbackDismissedEventProps,
  type EvidenceExperienceFeedbackRatingSubmittedEventProps,
  type EvidenceExperienceFeedbackSnoozedEventProps,
  type EvidenceExperienceFeedbackSubmittedEventProps,
  type EvidenceExperienceFeedbackViewedEventProps,
  type EvidenceJurisdictionChangedEventProps,
  type EvidenceLanguageChangedEventProps,
  type EvidenceLiveSuggestionsToggledEventProps,
  type EvidenceNhsContactSalesSubmittedEventProps,
  type EvidenceOnboardingDismissedEventProps,
  type EvidenceOnboardingSkippedEventProps,
  type EvidenceReferenceFeedbackSubmittedEventProps,
  type EvidenceReferenceScrollClickedEventProps,
  type EvidenceReferencesSectionToggledEventProps,
  type EvidenceRequestAccessClickedEventProps,
  type EvidenceResponseFeedbackSubmittedEventProps,
  type EvidenceSharedChatOpenedEventProps,
  type EvidenceShareLinkCopiedEventProps,
  type EvidenceShareModalOpenedEventProps,
  type EvidenceShareSocialClickedEventProps,
  type EvidenceSignUpWallViewedEventProps,
  type EvidenceSourceClickedEventProps,
  type EvidenceSourceControlToggledEventProps,
  type EvidenceSpecialtyChangedEventProps,
  type EvidenceSuggestionClickedEventProps,
  type EvidenceToastShownEventProps,
  type EvidenceVerbClickedEventProps,
} from '@/models/analytics/features/evidence';
import {
  type EvidencePromptsBarDismissedEventProps,
  type EvidencePromptsBarExpandedEventProps,
  type EvidencePromptsBarShownEventProps,
} from '@/models/analytics/features/evidence-prompts-bar';
import {
  type EvidenceShortcutRunEventProps,
  type EvidenceShortcutSaveToggledEventProps,
  type EvidenceShortcutSearchUsedEventProps,
  type EvidenceShortcutsOpenedEventProps,
  type EvidenceSourcesOpenedEventProps,
} from '@/models/analytics/features/evidence-shortcuts';
import {
  type EvidenceSummarySuggestionDismissedEventProps,
  type EvidenceSummarySuggestionGeneratedEventProps,
  type EvidenceSummarySuggestionShownEventProps,
} from '@/models/analytics/features/evidence-summary-suggestion';
import {
  type ExportUpdatedEventProps,
  type NotificationUpdatedEventProps,
} from '@/models/analytics/features/export-settings';
import { type FeedbackUpdatedEventProps } from '@/models/analytics/features/feedback';
import {
  type ForceUpdateDeferredEventProps,
  type ForceUpdateInitiatedEventProps,
  type ForceUpdateViewedEventProps,
} from '@/models/analytics/features/forceUpdate';
import {
  type LetterSenderAttachmentRejectedEventProps,
  type LetterSenderCcToggledEventProps,
  type LetterSenderFilePreviewedEventProps,
  type LetterSenderFileRemovedEventProps,
  type LetterSenderFileUploadedEventProps,
  type LetterSenderModalClosedEventProps,
  type LetterSenderModalOpenedEventProps,
  type LetterSenderProviderSearchedEventProps,
  type LetterSenderRecipientAddedEventProps,
  type LetterSenderRecipientRemovedEventProps,
  type LetterSenderSendFailedEventProps,
  type LetterSenderSentEventProps,
} from '@/models/analytics/features/letter-sender';
import {
  type ReplacementsUploadedEventProps,
  type SnippetsUploadedEventProps,
} from '@/models/analytics/features/memory';
import {
  type MiniModeClosedEventProps,
  type MiniModeControlAttemptedEventProps,
  type MiniModeControlClickedEventProps,
  type MiniModeNotepadEditedEventProps,
  type MiniModeOpenedEventProps,
  type MiniModePreferenceChangedEventProps,
} from '@/models/analytics/features/mini-mode';
import {
  type NoteAutoRetryDecidedEventProps,
  type NoteCopiedEventProps,
  type NoteEditorRenderedEventProps,
  type NoteEditStartedEventProps,
  type NoteFailedEventProps,
  type NoteFailureOverlayClearedEventProps,
  type NoteFailurePresentedEventProps,
  type NoteGenerationCompletedEventProps,
  type NoteGenerationFailedEventProps,
  type NoteGenerationStartedEventProps,
  type NoteInsertAppliedEventProps,
  type NotePushFailedEventProps,
  type NoteSectionCreatedEventProps,
  type NoteSectionDeletedEventProps,
  type NoteStartedEventProps,
} from '@/models/analytics/features/notes';
import {
  type OfflineRecordingDeletedEventProps,
  type OfflineRecordingQueuedEventProps,
  type OfflineRecordingStartedEventProps,
  type OfflineSessionDeleteEnqueueFailedEventProps,
  type OfflineSessionDeleteQueuedEventProps,
  type OfflineSessionDeleteReplayFailedEventProps,
  type OfflineSyncCompletedEventProps,
  type OfflineSyncDeferredEventProps,
  type OfflineSyncFailedEventProps,
} from '@/models/analytics/features/offline-recording';
import {
  type OnboardingAuthenticationCompletedEventProps,
  type OnboardingCompletedEventProps,
  type OnboardingDocumentCreatedEventProps,
  type OnboardingDocumentsSelectedEventProps,
  type OnboardingEvidenceAcceptedEventProps,
  type OnboardingEvidenceDiscardedEventProps,
  type OnboardingEvidenceQueriedEventProps,
  type OnboardingLearnBasicsCompletedEventProps,
  type OnboardingLearnBasicsSkippedEventProps,
  type OnboardingNoteStyleUpdatedEventProps,
  type OnboardingStartedEventProps,
  type OnboardingStepCompletedEventProps,
  type OnboardingStepSkippedEventProps,
  type OnboardingTemplateCompletedEventProps,
  type OnboardingTemplateReviewedEventProps,
  type OnboardingTemplateStartedEventProps,
  type OnboardingUpdatedEventProps,
  type OrganisationSurveySubmittedEventProps,
  type SignupPhoneFieldViewedEventProps,
} from '@/models/analytics/features/onboarding';
import {
  type AutoLinkSessionsPreferenceChangedEventProps,
  type LinkedSessionsSpotlightDismissedEventProps,
  type PatientAutoExtractPreferenceChangedEventProps,
  type PatientLinkingTimeframeDaysChangedEventProps,
  type PatientLinkingTimeframeSelectedEventProps,
  type PatientLinkingToggledEventProps,
} from '@/models/analytics/features/past-context';
import {
  type PatientContextToggleOffEventProps,
  type PatientContextToggleOnEventProps,
  type PatientCreatedEventProps,
  type PatientDeleteBlockedEventProps,
  type PatientDeleteClickedEventProps,
  type PatientDirectoryEnableClickedEventProps,
  type PatientDocumentLinkedEventProps,
  type PatientDocumentUnlinkedEventProps,
  type PatientDocumentUploadedEventProps,
  type PatientExtractionModalOpenedEventProps,
  type PatientExtractionReviewedEventProps,
  type PatientFieldEditedEventProps,
  type PatientLinkedEventProps,
  type PatientListViewedEventProps,
  type PatientProfileOpenedEventProps,
  type PatientProfileTabViewedEventProps,
  type PatientRecapPopoverOpenedEventProps,
  type PatientRecapSourceSessionClickedEventProps,
  type PatientSelectedEventProps,
  type PatientSessionExpandedEventProps,
  type PatientSummaryCtaShownEventProps,
  type PatientSummarySmsSendAttemptedEventProps,
  type PatientSummarySmsSentEventProps,
  type PatientUnlinkedEventProps,
  type PatientUpdatedEventProps,
  type PatientViewedEventProps,
} from '@/models/analytics/features/patient';
import {
  type ImproveNotesTaskCompletedEventProps,
  type NoteComparisonViewedEventProps,
  type PostNoteExperienceCompletedEventProps,
  type SpotlightAdvancedEventProps,
  type SpotlightClosedEventProps,
  type TrialDaysUnlockedEventProps,
} from '@/models/analytics/features/post-note-quality';
import {
  type PrechartSentToContextEventProps,
  type PrechartViewedEventProps,
} from '@/models/analytics/features/prechart';
import { type ProductTourViewedEventProps } from '@/models/analytics/features/product-tour';
import {
  type ReferralCompletedEventProps,
  type ReferralCreatedEventProps,
  type ReferralSharedEventProps,
  type ReferralViewedEventProps,
} from '@/models/analytics/features/referral';
import {
  type SettingsModalClosedEventProps,
  type SettingsModalOpenedEventProps,
  type SettingsModalSectionChangedEventProps,
  type SettingsModalUnknownRouteEventProps,
  type SettingsUpdatedEventProps,
} from '@/models/analytics/features/settings';
import {
  type SnippetCreatedEventProps,
  type SnippetDeletedEventProps,
  type SnippetUpdatedEventProps,
  type SnippetUploadedEventProps,
} from '@/models/analytics/features/snippets';
import {
  type SourceRecommendationAppliedEventProps,
  type SourceRecommendationCustomisedEventProps,
  type SourceRecommendationDismissedEventProps,
  type SourceRecommendationShownEventProps,
} from '@/models/analytics/features/source-recommendation';
import {
  type ScribeStyleUpdatedEventProps,
  type VoiceStyleUpdatedEventProps,
} from '@/models/analytics/features/styles';
import {
  type SurveySubmittedEventProps,
  type SurveyUpdatedEventProps,
  type SurveyViewedEventProps,
} from '@/models/analytics/features/survey';
import {
  type TaskCompletedEventProps,
  type TaskCreatedEventProps,
  type TaskDeletedEventProps,
  type TaskListViewedEventProps,
  type TaskNotificationClickedEventProps,
  type TaskUpdatedEventProps,
  type TaskViewedEventProps,
} from '@/models/analytics/features/tasks';
import {
  type TeamCreatedEventProps,
  type TeamEvidenceAccessUpdatedEventProps,
  type TeamHeidiIiAccessUpdatedEventProps,
  type TeamMemberAcceptedEventProps,
  type TeamMemberCreatedEventProps,
  type TeamMemberDeletedEventProps,
  type TeamUpdatedEventProps,
} from '@/models/analytics/features/team';
import {
  type TemplateCompletedEventProps,
  type TemplateConvertedProps,
  type TemplateCreatedProps,
  type TemplateCreationRecordCreatedProps,
  type TemplateDeletedEventProps,
  type TemplateEditorUpdatedProps,
  type TemplateFailedEventProps,
  type TemplateGenerationProps,
  type TemplateOpenedProps,
  type TemplateQualityGateEventProps,
  type TemplateRevertedEventProps,
  type TemplateSearchedEventProps,
  type TemplateUpdatedProps,
  type TemplateViewedEventProps,
} from '@/models/analytics/features/templates';
import {
  type AccountDeletedEventProps,
  type ApplicationInstalledEventProps,
  type AuthenticationClickedEventProps,
  type AuthManualControlEventProps,
  type HelpViewedEventProps,
  type SignOutEventProps,
  type SignupConversionEventProps,
  type UserClickedEventProps,
  type UserSignedInEventProps,
  type UserSignedUpEventProps,
  type UserStartedEventProps,
  type UserUpdatedEventProps,
} from '@/models/analytics/features/user';
import type { TaskDelegatedEventProps } from '@heidi/agents/contracts';
import { type TrackingEventName } from '@heidi/analytics/events';
import type {
  CommsSetupCompletedEventProps,
  CommsSetupStepCompletedEventProps,
  ObjectiveConfiguredEventProps,
} from '@heidi/calls/contracts';
import type {
  LibraryCollectionCreatedEventProps,
  LibraryDocumentAddedToCollectionEventProps,
  LibraryDocumentScopeChangedEventProps,
  LibraryDocumentUploadedEventProps,
  LibraryLinkClickedEventProps,
  LibraryUploadNudgeClickedEventProps,
} from '@heidi/evidence/contracts';
import {
  type EvidenceOnboardingSavedEventProps,
  type EvidenceOnboardingViewedEventProps,
} from '@heidi/evidence/contracts';
import { type NudgeProtocolsToggledEventProps } from '@heidi/fde/contracts';
import {
  type IntegrationFailedEventProps,
  type IntegrationNoteSyncedEventProps,
  type IntegrationStartedEventProps,
} from '@heidi/integrations/ehr/contracts';
import {
  type PatientSummaryEmailSendAttemptedEventProps,
  type PatientSummaryEmailSentEventProps,
} from '@heidi/patients/contracts';
import {
  type ContextLinkedEventProps,
  type ContextPreviewedEventProps,
  type ContextUnlinkedEventProps,
  type ExperimentProps,
} from '@heidi/session/contracts';
import { type FormTemplateFieldsUpdatedEventProps } from '@heidi/templates';
import { type BookDemoFormSubmittedEventProps } from './features/book-demo';
import {
  type DeviceEventProps,
  type HardwareBleReconnectedEventProps,
  type HardwareBluetoothPermissionResultEventProps,
  type HardwareBluetoothSettingsOpenedEventProps,
  type HardwareBulkSyncCancelledEventProps,
  type HardwareBulkSyncCompletedEventProps,
  type HardwareBulkSyncDbMigrationAppliedEventProps,
  type HardwareBulkSyncDbMigrationFailedEventProps,
  type HardwareBulkSyncFailedEventProps,
  type HardwareBulkSyncFileCrcCheckedEventProps,
  type HardwareBulkSyncFileFailedEventProps,
  type HardwareBulkSyncFileRetryEventProps,
  type HardwareBulkSyncPhaseChangedEventProps,
  type HardwareBulkSyncResumedEventProps,
  type HardwareBulkSyncStartedEventProps,
  type HardwareBulkSyncTaskFailedEventProps,
  type HardwareBulkSyncTriggeredEventProps,
  type HardwareContactSupportTappedEventProps,
  type HardwareDetailViewedEventProps,
  type HardwareDeviceAlreadyPairedEventProps,
  type HardwareDeviceConnectedEventProps,
  type HardwareDeviceConnectionFailedEventProps,
  type HardwareDeviceDisconnectedEventProps,
  type HardwareDeviceLinkedEventProps,
  type HardwareDeviceLinkFailedEventProps,
  type HardwareDeviceNotFoundEventProps,
  type HardwareDeviceReconnectAttemptEventProps,
  type HardwareDeviceReconnectTimeoutEventProps,
  type HardwareDeviceRecordingStoppedEventProps,
  type HardwareDeviceRemovedEventProps,
  type HardwareDeviceRemoveFailedEventProps,
  type HardwareDeviceRemoveTappedEventProps,
  type HardwareDeviceScanStartedEventProps,
  type HardwareErrorRecoveryActionTakenEventProps,
  type HardwareErrorShownEventProps,
  type HardwareFileDecryptionFailedEventProps,
  type HardwareFileDecryptionSkippedEventProps,
  type HardwareFileDecryptionSuccessEventProps,
  type HardwareInputModeSelectedEventProps,
  type HardwareLearnMoreTappedEventProps,
  type HardwareLowBatteryShownEventProps,
  type HardwareRecordingChunkUploadFailedEventProps,
  type HardwareRecordingFirstChunkUploadedEventProps,
  type HardwareRecordingInterruptedEventProps,
  type HardwareRecordingPauseEventProps,
  type HardwareRecordingPauseFailedEventProps,
  type HardwareRecordingRecoveredEventProps,
  type HardwareRecordingResumeEventProps,
  type HardwareRecordingResumeFailedEventProps,
  type HardwareRecordingStartBlockedEventProps,
  type HardwareRecordingStartEventProps,
  type HardwareRecordingStartFailedEventProps,
  type HardwareRecordingStopEventProps,
  type HardwareRecordingStopFailedEventProps,
  type HardwareRecoveryCompletedEventProps,
  type HardwareRecoveryFailedEventProps,
  type HardwareRetryConnectionTappedEventProps,
  type HardwareSessionCreatedEventProps,
  type HardwareSetupCompletedEventProps,
  type HardwareSetupStartedEventProps,
  type HardwareSetupStepViewedEventProps,
  type HardwareTransportFallbackEventProps,
  type HardwareUsbAccessDeniedEventProps,
  type HardwareUsbModeToggledEventProps,
  type HardwareUsbModeToggleFailedEventProps,
  type HardwareWifiSetupCompletedEventProps,
  type HardwareWifiSetupFailedEventProps,
  type HardwareWifiSetupStartedEventProps,
  type HardwareWifiTransferRequestedEventProps,
  type NativeFirmwareApplyCompleteEventProps,
  type NativeFirmwareApplyFailedEventProps,
  type NativeFirmwareApplyStartEventProps,
  type NativeFirmwareBatteryCheckFailedEventProps,
  type NativeFirmwareDownloadCompleteEventProps,
  type NativeFirmwareDownloadFailedEventProps,
  type NativeFirmwareDownloadStartEventProps,
  type NativeFirmwarePostUpdateReconnectedEventProps,
  type NativeFirmwareUpdateAvailableEventProps,
  type NativeFirmwareUpdateCheckFailedEventProps,
  type NativeFirmwareUpdateCheckStartEventProps,
  type NativeFirmwareUpdateTappedEventProps,
  type NativeFirmwareUpgradeCompleteEventProps,
  type NativeFirmwareUpgradeFailedEventProps,
  type NativeFirmwareUpgradeStartEventProps,
  type NativeFirmwareUploadCompleteEventProps,
  type NativeFirmwareUploadFailedEventProps,
  type NativeFirmwareUploadStartEventProps,
  type NativeFirmwareUpToDateEventProps,
} from './features/chronicle';
import {
  type CoachMarkEventProps,
  type DemoCompletedEventProps,
  type FirstDocumentCreatedEventProps,
} from './features/demo-flow';
import {
  type ExportContentSavedEventProps,
  type ExportSettingToggledEventProps,
} from './features/export-settings';
import { type FeedbackSubmittedEventProps } from './features/feedback';
import {
  type RemoteIntroPromoClickedEventProps,
  type RemoteIntroPromoDismissedEventProps,
  type RemoteIntroPromoViewedEventProps,
} from './features/growth-remote-promo';
import {
  type EhrStartedEventProps,
  type GdtFileParsedEventProps,
  type HaikuTabChangedEventProps,
  type IntegrationCompletedEventProps,
  type IntegrationCreatedEventProps,
  type IntegrationDataCompletedEventProps,
  type IntegrationDataFailedEventProps,
  type IntegrationDataRetrievedEventProps,
  type IntegrationLinkedEventProps,
  type IntegrationPatientSelectedEventProps,
  type IntegrationSessionCreatedEventProps,
  type IntegrationSubmittedEventProps,
  type IntegrationUnlinkedEventProps,
  type IntegrationUpdatedEventProps,
  type IntegrationUserSearchedEventProps,
  type IntegrationWidgetAuthStepErroredEventProps,
  type IntegrationWidgetAuthStepMiscEventProps,
  type PatientSearchedEventProps,
  type PushWarningResolvedEventProps,
  type PushWarningViewedEventProps,
  type SectionLinkedEventProps,
  type TemplateSelectedEventProps,
  type TokenGeneratedEventProps,
  type TranscriptionSkippedEventProps,
  type TranscriptionStartedEventProps,
  type TranscriptionStoppedEventProps,
} from './features/integrations';
import {
  type CountrySelectedEventProps,
  type LanguageSelectedEventProps,
} from './features/locale-selection';
import {
  type NoteContentPastedEventProps,
  type NoteCreatedEventProps,
  type NoteDisplayedEventProps,
  type NoteRevertedEventProps,
  type NoteSelectedEventProps,
  type NoteSelectionSkippedEventProps,
  type NoteUpdatedEventProps,
} from './features/notes';
import {
  type EarlyMeetingChoiceSelectedEventProps,
  type EarlyMeetingRegionNotSupportedEventProps,
  type MeetingBookedEventProps,
  type MeetingBookingFailedEventProps,
  type MeetingBookingSubmittedEventProps,
  type MeetingFallbackToRequestDemoEventProps,
  type MeetingSlotSelectedEventProps,
  type OnboardingDemoVariantViewedEventProps,
  type PhoneDemoBookedEventProps,
  type SdrDemoOptInChangedEventProps,
  type SdrDemoRequestedEventProps,
  type WebinarOptInChangedEventProps,
  type WebinarRegistrationSubmittedEventProps,
  type WebinarTimeslotSelectedEventProps,
} from './features/onboarding-hubspot';
import {
  type CancelFlowOpenedEventProps,
  type CancelOfferEventProps,
  type CancelReasonSelectedEventProps,
  type CheckoutStartedEventProps,
  type PaymentEventProps,
  type PaywallEventProps,
} from './features/payments';
import { type SessionLayoutSwitchedEventProps } from './features/session-layout';
import {
  type SessionMergeConfirmedEventProps,
  type SessionMergeDissolveCancelledEventProps,
  type SessionMergeDissolveConfirmedEventProps,
  type SessionMergeDissolvePromptViewedEventProps,
  type SessionMergeFailedEventProps,
  type SessionMergeIgnoredEventProps,
  type SessionMergeOnboardingAcceptedEventProps,
  type SessionMergeOnboardingDismissedEventProps,
  type SessionMergeOnboardingViewedEventProps,
  type SessionMergeRelinkedEventProps,
  type SessionMergeReviewDismissedEventProps,
  type SessionMergeReviewViewedEventProps,
  type SessionMergeTabViewedEventProps,
  type SessionMergeUnlinkCancelledEventProps,
  type SessionMergeUnlinkConfirmedEventProps,
  type SessionMergeUnlinkedEventProps,
  type SessionMergeUnlinkPromptViewedEventProps,
} from './features/session-merge';
import {
  type SessionLinkShareOtpFailedEventProps,
  type SessionLinkShareOtpVerifiedEventProps,
  type ShareAccessChangedEventProps,
  type ShareAccessRevokedEventProps,
  type ShareButtonClickedEventProps,
  type ShareEditRequestedEventProps,
  type ShareExternalConfirmedEventProps,
  type ShareExternalEmailAddedEventProps,
  type ShareExternalEmailRemovedEventProps,
  type ShareExternalInlineCancelledEventProps,
  type ShareExternalInlineOpenedEventProps,
  type ShareExternalLinkSendSubmittedEventProps,
  type ShareExternalRevealOpenedEventProps,
  type ShareLinkCopiedEventProps,
  type ShareLinkGeneratedEventProps,
  type ShareMemberAddedEventProps,
  type ShareMemberSearchedEventProps,
  type ShareModalOpenedEventProps,
  type ShareNotificationClickedEventProps,
  type ShareNotificationReceivedEventProps,
  type ShareSavedEventProps,
} from './features/session-sharing';
import {
  type ActiveSessionControlClickedEventProps,
  type ActiveSessionIndicatorClickedEventProps,
  type ActiveSessionNavigationEventProps,
  type CopySettingsUpdatedEventProps,
  type PersonalizationUpdatedEventProps,
  type ScheduleSyncedEventProps,
  type ScheduleUploadCompletedEventProps,
  type ScheduleUploadProcessingFailedEventProps,
  type ScheduleUploadPromoClickedEventProps,
  type ScheduleUploadPromoViewedEventProps,
  type ScheduleUploadSessionsCreatedEventProps,
  type ScheduleUploadStartedEventProps,
  type ScheduleViewedEventProps,
  type SessionCopiedEventProps,
  type SessionCreatedEventProps,
  type SessionDeletedEventProps,
  type SessionDetailResumeStartedEventProps,
  type SessionMarkedAsContextOnlyEventProps,
  type SessionSearchUsageBaselineEventProps,
  type SessionsFilterAppliedEventProps,
  type SessionsListPaginatedEventProps,
  type SessionsScopeChangedEventProps,
  type SessionsSortChangedEventProps,
  type SessionTabSwitchedEventProps,
  type SessionTabViewedEventProps,
  type SessionUpdatedEventProps,
  type SessionViewedEventProps,
  type TranscriptDeletedEventProps,
} from './features/sessions';
import {
  type SubscriptionCreatedEventProps,
  type TrialCompletedEventProps,
  type TrialEventProps,
} from './features/trial';
import {
  type ButtonEventProps,
  type FieldSelectedEventProps,
  type ModalDismissedEventProps,
  type ModalEventProps,
  type ScreenEventProps,
  type UiInteractionEventProps,
} from './features/ui';
// ——— Event Names & Discriminated Union ——————————————————————————————————————————————
export { TrackingEventName } from '@heidi/analytics/events';

export type TrackingEventV2 =
  // UI
  | {
      event_name: TrackingEventName.UiInteraction;
      props: UiInteractionEventProps;
    }
  | {
      event_name: TrackingEventName.ButtonClicked;
      props: ButtonEventProps;
    }
  | {
      event_name: TrackingEventName.ScreenViewed;
      props: ScreenEventProps;
    }
  | {
      event_name: TrackingEventName.ModalViewed;
      props: ModalEventProps;
    }
  | {
      event_name: TrackingEventName.ModalDismissed;
      props: ModalDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.FieldSelected;
      props: FieldSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.PanelOpened;
      props: PanelEventProps;
    }
  | {
      event_name: TrackingEventName.PanelClosed;
      props: PanelEventProps;
    }
  | {
      event_name: TrackingEventName.ToggleUpdated;
      props: ToggleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.FormTemplateFieldsUpdated;
      props: FormTemplateFieldsUpdatedEventProps;
    }
  // Chat (object_action pattern for Evidence)
  | {
      event_name: TrackingEventName.MessageSubmitted;
      props: MessageSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.MessageReceived;
      props: MessageReceivedEventProps;
    }
  | {
      event_name: TrackingEventName.MessageFailed;
      props: MessageFailedEventProps;
    }
  | {
      event_name: TrackingEventName.MessageAborted;
      props: MessageAbortedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatCreated;
      props: ChatCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatViewed;
      props: ChatViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatDeleted;
      props: ChatDeletedEventProps;
    }
  // Authentication
  | {
      event_name: TrackingEventName.AuthenticationCompleted;
      props: OnboardingAuthenticationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ForcedLogout;
      props: ForcedLogoutEventProps;
    }

  // Notes
  | {
      event_name: TrackingEventName.NoteCreated;
      props: NoteCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteSelected;
      props: NoteSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.ImproveNotesTaskCompleted;
      props: ImproveNotesTaskCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.PostNoteExperienceCompleted;
      props: PostNoteExperienceCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteComparisonViewed;
      props: NoteComparisonViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TrialDaysUnlocked;
      props: TrialDaysUnlockedEventProps;
    }
  | {
      event_name: TrackingEventName.SpotlightAdvanced;
      props: SpotlightAdvancedEventProps;
    }
  | {
      event_name: TrackingEventName.SpotlightClosed;
      props: SpotlightClosedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteDisplayed;
      props: NoteDisplayedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteSelectionSkipped;
      props: NoteSelectionSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteUpdated;
      props: NoteUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteReverted;
      props: NoteRevertedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteContentPasted;
      props: NoteContentPastedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionCopied;
      props: SessionCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.CopySettingsUpdated;
      props: CopySettingsUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.AssistantPanelAction;
      props: AssistantPanelActionEventProps;
    }
  // Onboarding
  | {
      event_name: TrackingEventName.OnboardingStarted;
      props?: OnboardingStartedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingStepCompleted;
      props?: OnboardingStepCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingStepSkipped;
      props: OnboardingStepSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.SignupPhoneFieldViewed;
      props: SignupPhoneFieldViewedEventProps;
    }
  | {
      event_name: TrackingEventName.OrganisationSurveySubmitted;
      props: OrganisationSurveySubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingCompleted;
      props?: OnboardingCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingTemplateStarted;
      props: OnboardingTemplateStartedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingTemplateCompleted;
      props: OnboardingTemplateCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingTemplateReviewed;
      props: OnboardingTemplateReviewedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingDocumentsSelected;
      props: OnboardingDocumentsSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingNoteStyleUpdated;
      props: OnboardingNoteStyleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingEvidenceQueried;
      props: OnboardingEvidenceQueriedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingEvidenceAccepted;
      props: OnboardingEvidenceAcceptedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingEvidenceDiscarded;
      props: OnboardingEvidenceDiscardedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingDocumentCreated;
      props: OnboardingDocumentCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingLearnBasicsCompleted;
      props: OnboardingLearnBasicsCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingLearnBasicsSkipped;
      props: OnboardingLearnBasicsSkippedEventProps;
    }

  // Account
  | {
      event_name: TrackingEventName.AccountCreated;
      props?: undefined;
    }
  // Session
  | {
      event_name: TrackingEventName.SessionCreated;
      props: SessionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionViewed;
      props?: SessionViewedEventProps;
    }
  // Transcription
  | {
      event_name: TrackingEventName.TranscriptionStarted;
      props?: TranscriptionStartedEventProps;
    }
  | {
      event_name: TrackingEventName.TranscriptionStopped;
      props?: TranscriptionStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.TranscriptionSkipped;
      props: TranscriptionSkippedEventProps;
    }
  // Live Transcription Indicator (Record Anywhere)
  | {
      event_name: TrackingEventName.ActiveSessionIndicatorClicked;
      props: ActiveSessionIndicatorClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ActiveSessionControlClicked;
      props: ActiveSessionControlClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ActiveSessionNavigation;
      props: ActiveSessionNavigationEventProps;
    }
  // Dictation
  | {
      event_name: TrackingEventName.DictationStarted;
      props: DictationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationCompleted;
      props: DictationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationErrored;
      props: DictationErroredEventProps;
    }
  | {
      event_name: TrackingEventName.LegacyInlineDictationCompleted;
      props: LegacyInlineDictationCompletedEventProps;
    }
  // Dictation CSAT
  | {
      event_name: TrackingEventName.DictationCsatSubmitted;
      props: DictationCsatSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationCsatDismissed;
      props: DictationCsatDismissedEventProps;
    }
  // Experiments
  // Account

  // Session
  | {
      event_name: TrackingEventName.SessionViewed;
      props?: undefined;
    }
  // Transcription

  // Experiments
  | {
      event_name: TrackingEventName.ExperimentStarted;
      props: ExperimentProps;
    }
  // Monetization
  | {
      event_name: TrackingEventName.PaywallViewed;
      props: PaywallEventProps;
    }
  // Monetization
  | {
      event_name: TrackingEventName.CheckoutStarted;
      props?: CheckoutStartedEventProps;
    }
  | {
      event_name: TrackingEventName.CancelFlowOpened;
      props: CancelFlowOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.CancelReasonSelected;
      props: CancelReasonSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.CancelOfferShown;
      props: CancelOfferEventProps;
    }
  | {
      event_name: TrackingEventName.CancelOfferAccepted;
      props: CancelOfferEventProps;
    }
  | {
      event_name: TrackingEventName.CancelOfferDeclined;
      props: CancelOfferEventProps;
    }
  | {
      event_name: TrackingEventName.PaymentStarted;
      props?: PaymentEventProps;
    }
  | {
      event_name: TrackingEventName.TrialStarted;
      props: TrialEventProps;
    }
  | {
      event_name: TrackingEventName.TrialCompleted;
      props: TrialCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.SubscriptionCreated;
      props: SubscriptionCreatedEventProps;
    }
  // Compliance
  | {
      event_name: TrackingEventName.PaymentCompleted;
      props: PaymentEventProps;
    }

  // Compliance
  | {
      event_name: TrackingEventName.ComplianceCheck;
      props: ComplianceEventProps;
    }
  // Auth
  | {
      event_name: TrackingEventName.UserSignedIn;
      props?: UserSignedInEventProps;
    }
  | {
      event_name: TrackingEventName.UserSignedUp;
      props?: UserSignedUpEventProps;
    }
  | {
      event_name: TrackingEventName.SignupConversion;
      props?: SignupConversionEventProps;
    }
  | {
      event_name: TrackingEventName.UserSignedOut;
      props?: SignOutEventProps;
    }
  | {
      event_name: TrackingEventName.AuthManualLinkCopied;
      props: AuthManualControlEventProps;
    }
  | {
      event_name: TrackingEventName.AuthManualOpenClicked;
      props: AuthManualControlEventProps;
    }
  | {
      event_name: TrackingEventName.AuthFallbackUsed;
      props: AuthManualControlEventProps;
    }
  // Feedback
  | {
      event_name: TrackingEventName.FeedbackSubmitted;
      props: FeedbackSubmittedEventProps;
    }
  // Force Update
  | {
      event_name: TrackingEventName.ForceUpdateViewed;
      props: ForceUpdateViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ForceUpdateDeferred;
      props: ForceUpdateDeferredEventProps;
    }
  | {
      event_name: TrackingEventName.ForceUpdateInitiated;
      props: ForceUpdateInitiatedEventProps;
    }

  // Documents
  | {
      event_name: TrackingEventName.DocumentExported;
      props: DocumentExportedEventProps;
    }
  | {
      event_name: TrackingEventName.FormFillingDownloaded;
      props: FormFillingDownloadedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentCreated;
      props: DocumentEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentGenerated;
      props: DocumentEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentUpdated;
      props: DocumentUpdatedEventProps;
    }
  // Queued document templates
  | {
      event_name: TrackingEventName.DocumentTemplateQueueLimitReached;
      props: DocumentTemplateQueueLimitReachedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentTemplateQueueReordered;
      props: DocumentTemplateQueueReorderedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionStartedWithDocumentQueue;
      props: SessionStartedWithDocumentQueueEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentGenerationStarted;
      props: QueuedDocumentGenerationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentGenerationFailed;
      props: QueuedDocumentGenerationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentGenerationCancelled;
      props: QueuedDocumentGenerationCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentTabTemplateSwapped;
      props: QueuedDocumentTabTemplateSwappedEventProps;
    }
  // Memory (Snippets & Replacements)
  | {
      event_name: TrackingEventName.SnippetsUploaded;
      props: SnippetsUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.ReplacementsUploaded;
      props: ReplacementsUploadedEventProps;
    }
  // Style Updates
  | {
      event_name: TrackingEventName.ScribeStyleUpdated;
      props: ScribeStyleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.VoiceStyleUpdated;
      props: VoiceStyleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.AmbientHeidiTriggered;
      props: import('@heidi/agents/contracts').AmbientHeidiTriggeredProps;
    }
  // Clinical Codes
  | {
      event_name: TrackingEventName.ClinicalCodeCopied;
      props: ClinicalCodeCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.ClinicalCodeClickable;
      props: ClinicalCodeClickableEventProps;
    }
  // Clinical Calculators
  | {
      event_name: TrackingEventName.CalculatorExecuted;
      props: CalculatorExecutedEventProps;
    }
  // Schedule
  | {
      event_name: TrackingEventName.ScheduleViewed;
      props: ScheduleViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleSynced;
      props: ScheduleSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadStarted;
      props: ScheduleUploadStartedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadCompleted;
      props: ScheduleUploadCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadProcessingFailed;
      props?: ScheduleUploadProcessingFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadSessionsCreated;
      props: ScheduleUploadSessionsCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadPromoViewed;
      props: ScheduleUploadPromoViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadPromoClicked;
      props: ScheduleUploadPromoClickedEventProps;
    }
  // Heidi Remote intro promo (GRO-2981)
  | {
      event_name: TrackingEventName.RemoteIntroPromoViewed;
      props: RemoteIntroPromoViewedEventProps;
    }
  | {
      event_name: TrackingEventName.RemoteIntroPromoClicked;
      props: RemoteIntroPromoClickedEventProps;
    }
  | {
      event_name: TrackingEventName.RemoteIntroPromoDismissed;
      props: RemoteIntroPromoDismissedEventProps;
    }
  // Session Search & Filter
  | {
      event_name: TrackingEventName.SessionSearchUsageBaseline;
      props: SessionSearchUsageBaselineEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsFilterApplied;
      props: SessionsFilterAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsScopeChanged;
      props: SessionsScopeChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsSortChanged;
      props: SessionsSortChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsListPaginated;
      props: SessionsListPaginatedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionUpdated;
      props: SessionUpdatedEventProps;
    }
  // Offline desktop recording
  | {
      event_name: TrackingEventName.OfflineRecordingStarted;
      props: OfflineRecordingStartedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineRecordingQueued;
      props: OfflineRecordingQueuedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineRecordingDeleted;
      props: OfflineRecordingDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSyncCompleted;
      props: OfflineSyncCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSyncFailed;
      props: OfflineSyncFailedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSyncDeferred;
      props: OfflineSyncDeferredEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSessionDeleteQueued;
      props: OfflineSessionDeleteQueuedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSessionDeleteEnqueueFailed;
      props: OfflineSessionDeleteEnqueueFailedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSessionDeleteReplayFailed;
      props: OfflineSessionDeleteReplayFailedEventProps;
    }
  // Patient
  | {
      event_name: TrackingEventName.ExtractionModalOpened;
      props: PatientExtractionModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ExtractionReviewed;
      props: PatientExtractionReviewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummarySmsSendAttempted;
      props: PatientSummarySmsSendAttemptedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummarySmsSent;
      props: PatientSummarySmsSentEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummaryEmailSendAttempted;
      props: PatientSummaryEmailSendAttemptedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummaryEmailSent;
      props: PatientSummaryEmailSentEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummaryCtaShown;
      props: PatientSummaryCtaShownEventProps;
    }
  | {
      event_name: TrackingEventName.PatientViewed;
      props: PatientViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientUpdated;
      props: PatientUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientCreated;
      props: PatientCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinked;
      props: PatientLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSelected;
      props: PatientSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientUnlinked;
      props: PatientUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientRecapSourceSessionClicked;
      props: PatientRecapSourceSessionClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientListViewed;
      props: PatientListViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientProfileOpened;
      props: PatientProfileOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientProfileTabViewed;
      props: PatientProfileTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSessionExpanded;
      props: PatientSessionExpandedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientFieldEdited;
      props: PatientFieldEditedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDocumentUploaded;
      props: PatientDocumentUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDocumentLinked;
      props: PatientDocumentLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDocumentUnlinked;
      props: PatientDocumentUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientRecapPopoverOpened;
      props: PatientRecapPopoverOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDirectoryEnableClicked;
      props: PatientDirectoryEnableClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDeleteClicked;
      props: PatientDeleteClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDeleteBlocked;
      props: PatientDeleteBlockedEventProps;
    }
  | {
      event_name: TrackingEventName.PrechartViewed;
      props: PrechartViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PrechartSentToContext;
      props: PrechartSentToContextEventProps;
    }
  | {
      event_name: TrackingEventName.PatientContextToggleOn;
      props: PatientContextToggleOnEventProps;
    }
  | {
      event_name: TrackingEventName.PatientContextToggleOff;
      props: PatientContextToggleOffEventProps;
    }
  // Tab sharing (Telehealth)
  | {
      event_name: TrackingEventName.TabSharingDoNoShowMessageAgain;
      props?: undefined;
    }
  | {
      event_name: TrackingEventName.TabSharingNotAvailableModalShown;
      props?: undefined;
    }
  // Templates
  | {
      event_name: TrackingEventName.TemplateCreated;
      props: TemplateCreatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateUpdated;
      props: TemplateUpdatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateCreationRecordCreated;
      props: TemplateCreationRecordCreatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateEditorUpdated;
      props: TemplateEditorUpdatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateGeneration;
      props: TemplateGenerationProps;
    }
  | {
      event_name: TrackingEventName.TemplateOpened;
      props: TemplateOpenedProps;
    }
  | {
      event_name: TrackingEventName.TemplateSelected;
      props: TemplateSelectedEventProps;
    }
  // Book Demo
  | {
      event_name: TrackingEventName.BookDemoFormSubmitted;
      props: BookDemoFormSubmittedEventProps;
    }

  // Book Demo
  | {
      event_name: TrackingEventName.BookDemoCountrySelected;
      props: BookDemoCountrySelectedEventProps;
    }
  | {
      event_name: TrackingEventName.BookDemoCliniciansSelected;
      props: BookDemoCliniciansSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.BookDemoNextClicked;
      props?: BookDemoNextClickedEventProps;
    }
  // Onboarding meeting / webinar (HubSpot)
  | {
      event_name: TrackingEventName.MeetingSlotSelected;
      props: MeetingSlotSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingBookingSubmitted;
      props: MeetingBookingSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingBooked;
      props: MeetingBookedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingBookingFailed;
      props: MeetingBookingFailedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingFallbackToRequestDemo;
      props: MeetingFallbackToRequestDemoEventProps;
    }
  | {
      event_name: TrackingEventName.WebinarOptInChanged;
      props: WebinarOptInChangedEventProps;
    }
  | {
      event_name: TrackingEventName.WebinarTimeslotSelected;
      props: WebinarTimeslotSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.WebinarRegistrationSubmitted;
      props: WebinarRegistrationSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.SdrDemoOptInChanged;
      props: SdrDemoOptInChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SdrDemoRequested;
      props: SdrDemoRequestedEventProps;
    }
  | {
      event_name: TrackingEventName.PhoneDemoBooked;
      props: PhoneDemoBookedEventProps;
    }
  | {
      event_name: TrackingEventName.EarlyMeetingChoiceSelected;
      props: EarlyMeetingChoiceSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.EarlyMeetingRegionNotSupported;
      props: EarlyMeetingRegionNotSupportedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingDemoVariantViewed;
      props: OnboardingDemoVariantViewedEventProps;
    }
  // Locale selection
  | {
      event_name: TrackingEventName.CountrySelected;
      props: CountrySelectedEventProps;
    }
  | {
      event_name: TrackingEventName.LanguageSelected;
      props: LanguageSelectedEventProps;
    }
  // Context Files
  | {
      event_name: TrackingEventName.ContextFileUploadStarted;
      props: ContextFileUploadStartedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFileUploaded;
      props: ContextFileUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFileUploadFailed;
      props: ContextFileUploadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFileDeleted;
      props: ContextFileDeletedEventProps;
    }
  // Integrations
  | {
      event_name: TrackingEventName.NoteSynced;
      props: IntegrationNoteSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteSyncFailed;
      props: IntegrationNoteSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.PushWarningViewed;
      props: PushWarningViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PushWarningResolved;
      props: PushWarningResolvedEventProps;
    }
  | {
      event_name: TrackingEventName.SectionLinked;
      props: SectionLinkedEventProps;
    }
  // Context (Previous Notes/Sessions linked to current session)
  | {
      event_name: TrackingEventName.ContextPreviewed;
      props: ContextPreviewedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextLinked;
      props: ContextLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextUnlinked;
      props: ContextUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.AutoLinkSessionsPreferenceChanged;
      props: AutoLinkSessionsPreferenceChangedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientAutoExtractPreferenceChanged;
      props: PatientAutoExtractPreferenceChangedEventProps;
    }
  | {
      event_name: TrackingEventName.LinkedSessionsSpotlightDismissed;
      props: LinkedSessionsSpotlightDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationLogsSpotlightDismissed;
      props: DictationLogsSpotlightDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.DictateTourBannerCtaClicked;
      props: DictateTourBannerCtaClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinkingToggled;
      props: PatientLinkingToggledEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinkingTimeframeSelected;
      props: PatientLinkingTimeframeSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinkingTimeframeDaysChanged;
      props: PatientLinkingTimeframeDaysChangedEventProps;
    }
  // Evidence
  | {
      event_name: TrackingEventName.EvidenceCategoryClicked;
      props: EvidenceCategoryClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceVerbClicked;
      props: EvidenceVerbClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSuggestionClicked;
      props: EvidenceSuggestionClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSignUpWallViewed;
      props: EvidenceSignUpWallViewedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSourceClicked;
      props: EvidenceSourceClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceRequestAccessClicked;
      props: EvidenceRequestAccessClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceContactSalesClicked;
      props: EvidenceContactSalesClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceNhsContactSalesSubmitted;
      props: EvidenceNhsContactSalesSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceResponseFeedbackSubmitted;
      props: EvidenceResponseFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceReferenceFeedbackSubmitted;
      props: EvidenceReferenceFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceReferenceScrollClicked;
      props: EvidenceReferenceScrollClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceReferencesSectionToggled;
      props: EvidenceReferencesSectionToggledEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShareModalOpened;
      props: EvidenceShareModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShareLinkCopied;
      props: EvidenceShareLinkCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShareSocialClicked;
      props: EvidenceShareSocialClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSharedChatOpened;
      props: EvidenceSharedChatOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceCopyConfirmation;
      props: EvidenceCopyConfirmationEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceCopied;
      props: EvidenceCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceCheckOtherSourcesClicked;
      props: EvidenceCheckOtherSourcesClickedEventProps;
    }
  | {
      event_name: TrackingEventName.FollowUpQuestionClicked;
      props: FollowUpQuestionClickedEventProps;
    }
  // Evidence - Chat events
  | {
      event_name: TrackingEventName.EvidenceChatSubmitted;
      props: EvidenceChatSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatCompleted;
      props: EvidenceChatCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatError;
      props: EvidenceChatErrorEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatAborted;
      props: EvidenceChatAbortedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatCtaClicked;
      props: EvidenceChatCtaClickedEventProps;
    }
  // Evidence - Onboarding events
  | {
      event_name: TrackingEventName.EvidenceOnboardingViewed;
      props: EvidenceOnboardingViewedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceOnboardingSaved;
      props: EvidenceOnboardingSavedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceOnboardingSkipped;
      props: EvidenceOnboardingSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceOnboardingDismissed;
      props: EvidenceOnboardingDismissedEventProps;
    }
  // Evidence - Settings events
  | {
      event_name: TrackingEventName.EvidenceLanguageChanged;
      props: EvidenceLanguageChangedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSpecialtyChanged;
      props: EvidenceSpecialtyChangedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceJurisdictionChanged;
      props: EvidenceJurisdictionChangedEventProps;
    }
  // Evidence - Toast events
  | {
      event_name: TrackingEventName.EvidenceToastShown;
      props: EvidenceToastShownEventProps;
    }
  | {
      event_name: TrackingEventName.CreditsConsumed;
      props: CreditsConsumedEventProps;
    }
  // Evidence - UI toggle events
  | {
      event_name: TrackingEventName.EvidenceSourceControlToggled;
      props: EvidenceSourceControlToggledEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceLiveSuggestionsToggled;
      props: EvidenceLiveSuggestionsToggledEventProps;
    }
  // Evidence - Experience Feedback events
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackViewed;
      props: EvidenceExperienceFeedbackViewedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackRatingSubmitted;
      props: EvidenceExperienceFeedbackRatingSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackSubmitted;
      props: EvidenceExperienceFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackSnoozed;
      props: EvidenceExperienceFeedbackSnoozedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackDismissed;
      props: EvidenceExperienceFeedbackDismissedEventProps;
    }
  // CPD
  | {
      event_name: TrackingEventName.CPDPageViewed;
      props: CPDPageViewedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDExported;
      props: CPDExportedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDDraftCreated;
      props: CPDDraftCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDDraftResumed;
      props: CPDDraftResumedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDDraftDeleted;
      props: CPDDraftDeletedEventProps;
    }
  // Chat Context Linking
  | {
      event_name: TrackingEventName.ChatContextAddSessionClicked;
      props: ChatContextAddSessionClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatContextAttachPatientClicked;
      props: ChatContextAttachPatientClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatContextAddFileClicked;
      props: ChatContextAddFileClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatMessageSentWithContext;
      props: ChatMessageSentWithContextEventProps;
    }
  | {
      event_name: TrackingEventName.DeeplinkTriggered;
      props: DeeplinkTriggeredEventProps;
    }
  | {
      event_name: TrackingEventName.TokenGenerated;
      props: TokenGeneratedEventProps;
    }
  // Haiku Integration
  | {
      event_name: TrackingEventName.HaikuTabChanged;
      props: HaikuTabChangedEventProps;
    }
  // Integrations
  | {
      event_name: TrackingEventName.IntegrationCreated;
      props: IntegrationCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationDataCompleted;
      props: IntegrationDataCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationDataFailed;
      props: IntegrationDataFailedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationDataRetrieved;
      props: IntegrationDataRetrievedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationPatientSelected;
      props: IntegrationPatientSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationSessionCreated;
      props: IntegrationSessionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationUpdated;
      props: IntegrationUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationUserSearched;
      props: IntegrationUserSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationWidgetAuthStepErrored;
      props: IntegrationWidgetAuthStepErroredEventProps;
    }
  | {
      event_name: TrackingEventName.GdtFileParsed;
      props: GdtFileParsedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationWidgetAuthStepMisc;
      props: IntegrationWidgetAuthStepMiscEventProps;
    }
  // Export Settings
  | {
      event_name: TrackingEventName.ExportSettingToggled;
      props: ExportSettingToggledEventProps;
    }
  | {
      event_name: TrackingEventName.ExportContentSaved;
      props: ExportContentSavedEventProps;
    }
  // Chronicle Device
  | {
      event_name: TrackingEventName.HardwareDeviceScanStarted;
      props: HardwareDeviceScanStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceConnected;
      props: HardwareDeviceConnectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceDisconnected;
      props: HardwareDeviceDisconnectedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpgradeStart;
      props: NativeFirmwareUpgradeStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpgradeComplete;
      props: NativeFirmwareUpgradeCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpgradeFailed;
      props: NativeFirmwareUpgradeFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncStarted;
      props: HardwareBulkSyncStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncCompleted;
      props: HardwareBulkSyncCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFailed;
      props: HardwareBulkSyncFailedEventProps;
    }
  // Remote hardware session/file tracing (iOS parity)
  | {
      event_name: TrackingEventName.HardwareSessionCreated;
      props: HardwareSessionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareFileDecryptionSkipped;
      props: HardwareFileDecryptionSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareFileDecryptionSuccess;
      props: HardwareFileDecryptionSuccessEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareFileDecryptionFailed;
      props: HardwareFileDecryptionFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFileCrcChecked;
      props: HardwareBulkSyncFileCrcCheckedEventProps;
    }
  // Hardware (iOS parity — APP-4173)
  | {
      event_name: TrackingEventName.HardwareSetupStarted;
      props: HardwareSetupStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBluetoothPermissionResult;
      props: HardwareBluetoothPermissionResultEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBluetoothSettingsOpened;
      props: HardwareBluetoothSettingsOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceConnectionFailed;
      props: HardwareDeviceConnectionFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceNotFound;
      props: HardwareDeviceNotFoundEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceAlreadyPaired;
      props: HardwareDeviceAlreadyPairedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceLinked;
      props: HardwareDeviceLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceLinkFailed;
      props: HardwareDeviceLinkFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareSetupCompleted;
      props: HardwareSetupCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareSetupStepViewed;
      props: HardwareSetupStepViewedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareInputModeSelected;
      props: HardwareInputModeSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStart;
      props: HardwareRecordingStartEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStartFailed;
      props: HardwareRecordingStartFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStartBlocked;
      props: HardwareRecordingStartBlockedEventProps;
    }
  | {
      event_name: TrackingEventName.DeviceEvent;
      props: DeviceEventProps;
    }
  | {
      event_name: TrackingEventName.ProcessLifecycle;
      props:
        | import('@snowtype/snowplow').ProcessLifecycle
        | import('@heidi/agents/contracts').AmbientHeidiUtteranceLifecycleProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingFirstChunkUploaded;
      props: HardwareRecordingFirstChunkUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingChunkUploadFailed;
      props: HardwareRecordingChunkUploadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStop;
      props: HardwareRecordingStopEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStopFailed;
      props: HardwareRecordingStopFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingPause;
      props: HardwareRecordingPauseEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingPauseFailed;
      props: HardwareRecordingPauseFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingResume;
      props: HardwareRecordingResumeEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingResumeFailed;
      props: HardwareRecordingResumeFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBleReconnected;
      props: HardwareBleReconnectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRecordingStopped;
      props: HardwareDeviceRecordingStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncTriggered;
      props: HardwareBulkSyncTriggeredEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncResumed;
      props: HardwareBulkSyncResumedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncCancelled;
      props: HardwareBulkSyncCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncPhaseChanged;
      props: HardwareBulkSyncPhaseChangedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFileFailed;
      props: HardwareBulkSyncFileFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFileRetry;
      props: HardwareBulkSyncFileRetryEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncTaskFailed;
      props: HardwareBulkSyncTaskFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingInterrupted;
      props: HardwareRecordingInterruptedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecoveryCompleted;
      props: HardwareRecoveryCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecoveryFailed;
      props: HardwareRecoveryFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingRecovered;
      props: HardwareRecordingRecoveredEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceReconnectAttempt;
      props: HardwareDeviceReconnectAttemptEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceReconnectTimeout;
      props: HardwareDeviceReconnectTimeoutEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareTransportFallback;
      props: HardwareTransportFallbackEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareErrorShown;
      props: HardwareErrorShownEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareErrorRecoveryActionTaken;
      props: HardwareErrorRecoveryActionTakenEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiTransferRequested;
      props: HardwareWifiTransferRequestedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateTapped;
      props: NativeFirmwareUpdateTappedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateCheckStart;
      props: NativeFirmwareUpdateCheckStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateCheckFailed;
      props: NativeFirmwareUpdateCheckFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateAvailable;
      props: NativeFirmwareUpdateAvailableEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpToDate;
      props: NativeFirmwareUpToDateEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareDownloadStart;
      props: NativeFirmwareDownloadStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareDownloadComplete;
      props: NativeFirmwareDownloadCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareDownloadFailed;
      props: NativeFirmwareDownloadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareBatteryCheckFailed;
      props: NativeFirmwareBatteryCheckFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUploadStart;
      props: NativeFirmwareUploadStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUploadComplete;
      props: NativeFirmwareUploadCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUploadFailed;
      props: NativeFirmwareUploadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareApplyStart;
      props: NativeFirmwareApplyStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareApplyComplete;
      props: NativeFirmwareApplyCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareApplyFailed;
      props: NativeFirmwareApplyFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwarePostUpdateReconnected;
      props: NativeFirmwarePostUpdateReconnectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDetailViewed;
      props: HardwareDetailViewedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRemoveTapped;
      props: HardwareDeviceRemoveTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRemoved;
      props: HardwareDeviceRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRemoveFailed;
      props: HardwareDeviceRemoveFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareUsbModeToggled;
      props: HardwareUsbModeToggledEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareUsbModeToggleFailed;
      props: HardwareUsbModeToggleFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareUsbAccessDenied;
      props: HardwareUsbAccessDeniedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRetryConnectionTapped;
      props: HardwareRetryConnectionTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareLowBatteryShown;
      props: HardwareLowBatteryShownEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareLearnMoreTapped;
      props: HardwareLearnMoreTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareContactSupportTapped;
      props: HardwareContactSupportTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiSetupStarted;
      props: HardwareWifiSetupStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiSetupCompleted;
      props: HardwareWifiSetupCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiSetupFailed;
      props: HardwareWifiSetupFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncDbMigrationApplied;
      props: HardwareBulkSyncDbMigrationAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncDbMigrationFailed;
      props: HardwareBulkSyncDbMigrationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ContactSalesFormOpened;
      props: ContactSalesFormOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ContactSalesFormSubmitted;
      props: ContactSalesFormSubmittedEventProps;
    }
  // Demo Flow
  | {
      event_name: TrackingEventName.DemoStarted;
      props: Record<string, never>;
    }
  | {
      event_name: TrackingEventName.DemoCompleted;
      props: DemoCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.CoachMarkViewed;
      props: CoachMarkEventProps;
    }
  | {
      event_name: TrackingEventName.CoachMarkActionTaken;
      props: CoachMarkEventProps;
    }
  | {
      event_name: TrackingEventName.FirstDocumentCreated;
      props: FirstDocumentCreatedEventProps;
    }
  // Library
  | {
      event_name: TrackingEventName.LibraryDocumentUploaded;
      props: LibraryDocumentUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryCollectionCreated;
      props: LibraryCollectionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryUploadNudgeClicked;
      props: LibraryUploadNudgeClickedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryLinkClicked;
      props: LibraryLinkClickedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryDocumentAddedToCollection;
      props: LibraryDocumentAddedToCollectionEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryDocumentScopeChanged;
      props: LibraryDocumentScopeChangedEventProps;
    }

  // ── Amplitude Migration: New Union Members ────────────────────────────────

  // Session (new)
  | {
      event_name: TrackingEventName.SessionDeleted;
      props: SessionDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TranscriptDeleted;
      props: TranscriptDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.PersonalizationUpdated;
      props: PersonalizationUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMarkedAsContextOnly;
      props: SessionMarkedAsContextOnlyEventProps;
    }
  | {
      event_name: TrackingEventName.SessionLayoutSwitched;
      props: SessionLayoutSwitchedEventProps;
    }

  // Document (new)
  | {
      event_name: TrackingEventName.DocumentCopied;
      props: DocumentCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentShared;
      props: DocumentSharedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentViewed;
      props: DocumentViewedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentDeleted;
      props: DocumentDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSynced;
      props: DocumentSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentEmailed;
      props: DocumentEmailedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionTabViewed;
      props: SessionTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionTabSwitched;
      props: SessionTabSwitchedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionDetailResumeStarted;
      props: SessionDetailResumeStartedEventProps;
    }

  // Notes (new)
  | {
      event_name: TrackingEventName.NoteStarted;
      props: NoteStartedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteFailed;
      props: NoteFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteAutoRetryDecided;
      props: NoteAutoRetryDecidedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteFailurePresented;
      props: NoteFailurePresentedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteFailureOverlayCleared;
      props: NoteFailureOverlayClearedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteGenerationStarted;
      props: NoteGenerationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteGenerationCompleted;
      props: NoteGenerationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteGenerationFailed;
      props: NoteGenerationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NotePushFailed;
      props: NotePushFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteCopied;
      props: NoteCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.SectionCreated;
      props: NoteSectionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.SectionDeleted;
      props: NoteSectionDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteEditorRendered;
      props: NoteEditorRenderedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteEditStarted;
      props: NoteEditStartedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteInsertApplied;
      props: NoteInsertAppliedEventProps;
    }

  // Templates (new)
  | {
      event_name: TrackingEventName.TemplateFailed;
      props: TemplateFailedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateCompleted;
      props: TemplateCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateViewed;
      props: TemplateViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateDeleted;
      props: TemplateDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateReverted;
      props: TemplateRevertedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateSearched;
      props: TemplateSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateQualityGate;
      props: TemplateQualityGateEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateConverted;
      props: TemplateConvertedProps;
    }

  // Tasks
  | {
      event_name: TrackingEventName.TaskCreated;
      props: TaskCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskCompleted;
      props: TaskCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskDeleted;
      props: TaskDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskUpdated;
      props: TaskUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskViewed;
      props: TaskViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskListViewed;
      props: TaskListViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskNotificationClicked;
      props: TaskNotificationClickedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskDelegated;
      props: TaskDelegatedEventProps;
    }

  // Chat & Evidence (new)
  | {
      event_name: TrackingEventName.MessageStopped;
      props: MessageStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceClicked;
      props: EvidenceClickedEventProps;
    }
  | {
      event_name: TrackingEventName.FeatureToggled;
      props: FeatureToggledEventProps;
    }

  // Survey
  | {
      event_name: TrackingEventName.SurveyViewed;
      props: SurveyViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SurveySubmitted;
      props: SurveySubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.SurveyUpdated;
      props: SurveyUpdatedEventProps;
    }

  // Team
  | {
      event_name: TrackingEventName.TeamMemberAccepted;
      props: TeamMemberAcceptedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamMemberCreated;
      props: TeamMemberCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamMemberDeleted;
      props: TeamMemberDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamCreated;
      props: TeamCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamUpdated;
      props: TeamUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamEvidenceAccessUpdated;
      props: TeamEvidenceAccessUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamHeidiIiAccessUpdated;
      props: TeamHeidiIiAccessUpdatedEventProps;
    }

  // Integrations (new)
  | {
      event_name: TrackingEventName.IntegrationFailed;
      props: IntegrationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationLinked;
      props: IntegrationLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationStarted;
      props: IntegrationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationCompleted;
      props: IntegrationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationSubmitted;
      props: IntegrationSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationUnlinked;
      props: IntegrationUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSearched;
      props: PatientSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.EhrStarted;
      props: EhrStartedEventProps;
    }

  // Audio
  | {
      event_name: TrackingEventName.AudioUploaded;
      props: AudioUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.AudioFailed;
      props: AudioFailedEventProps;
    }
  | {
      event_name: TrackingEventName.AudioStopped;
      props: AudioStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.RecordingStopFailed;
      props: RecordingStopFailedEventProps;
    }
  | {
      event_name: TrackingEventName.AudioWarningViewed;
      props: AudioWarningEventProps;
    }
  | {
      event_name: TrackingEventName.AudioWarningAccepted;
      props: AudioWarningEventProps;
    }

  // Mini-mode (PiP recording indicator)
  | {
      event_name: TrackingEventName.MiniModeOpened;
      props: MiniModeOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeClosed;
      props: MiniModeClosedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeControlClicked;
      props: MiniModeControlClickedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeControlAttempted;
      props: MiniModeControlAttemptedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeNotepadEdited;
      props: MiniModeNotepadEditedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModePreferenceChanged;
      props: MiniModePreferenceChangedEventProps;
    }
  | {
      event_name: TrackingEventName.NudgeProtocolsToggled;
      props: NudgeProtocolsToggledEventProps;
    }

  // Context (new)
  | {
      event_name: TrackingEventName.ContextUploaded;
      props: ContextUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFailed;
      props: ContextFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextDeleted;
      props: ContextDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextUpdated;
      props: ContextUpdatedEventProps;
    }

  // Snippets
  | {
      event_name: TrackingEventName.SnippetCreated;
      props: SnippetCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.SnippetUpdated;
      props: SnippetUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.SnippetDeleted;
      props: SnippetDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.SnippetUploaded;
      props: SnippetUploadedEventProps;
    }

  // Account & User (new)
  | {
      event_name: TrackingEventName.AccountDeleted;
      props: AccountDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.HelpViewed;
      props: HelpViewedEventProps;
    }
  | {
      event_name: TrackingEventName.AuthenticationClicked;
      props: AuthenticationClickedEventProps;
    }
  | {
      event_name: TrackingEventName.UserUpdated;
      props: UserUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.UserStarted;
      props: UserStartedEventProps;
    }
  | {
      event_name: TrackingEventName.UserClicked;
      props: UserClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ApplicationInstalled;
      props: ApplicationInstalledEventProps;
    }

  // Feedback (new)
  | {
      event_name: TrackingEventName.FeedbackUpdated;
      props: FeedbackUpdatedEventProps;
    }

  // Onboarding (new)
  | {
      event_name: TrackingEventName.OnboardingUpdated;
      props: OnboardingUpdatedEventProps;
    }

  // Diarisation
  | {
      event_name: TrackingEventName.DiarisedTranscriptViewed;
      props: DiarisedTranscriptViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SpeakerAttributionUpdated;
      props: SpeakerAttributionUpdatedEventProps;
    }

  // Settings
  | {
      event_name: TrackingEventName.SettingsUpdated;
      props: SettingsUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalOpened;
      props: SettingsModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalSectionChanged;
      props: SettingsModalSectionChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalClosed;
      props: SettingsModalClosedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalUnknownRoute;
      props: SettingsModalUnknownRouteEventProps;
    }

  // Appearance / Theme
  | {
      event_name: TrackingEventName.AppearanceModeChanged;
      props: AppearanceModeChangedEventProps;
    }

  // Export & Notification (new)
  | {
      event_name: TrackingEventName.ExportUpdated;
      props: ExportUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.NotificationUpdated;
      props: NotificationUpdatedEventProps;
    }

  // Referral
  | {
      event_name: TrackingEventName.ReferralCompleted;
      props: ReferralCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ReferralShared;
      props: ReferralSharedEventProps;
    }
  | {
      event_name: TrackingEventName.ReferralViewed;
      props: ReferralViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ReferralCreated;
      props: ReferralCreatedEventProps;
    }

  // Product Tour
  | {
      event_name: TrackingEventName.ProductTourViewed;
      props: ProductTourViewedEventProps;
    }

  // Errors
  | {
      event_name: TrackingEventName.OnboardingErrorViewed;
      props: OnboardingErrorViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ErrorViewed;
      props: ErrorViewedEventProps;
    }

  // Community
  | {
      event_name: TrackingEventName.CommunityViewed;
      props: CommunityViewedEventProps;
    }
  | {
      event_name: TrackingEventName.CommunitySearched;
      props: CommunitySearchedEventProps;
    }
  | {
      event_name: TrackingEventName.CommunityCreated;
      props: CommunityCreatedEventProps;
    }

  // Direct Sharing
  | {
      event_name: TrackingEventName.ShareButtonClicked;
      props: ShareButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareModalOpened;
      props: ShareModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareMemberSearched;
      props: ShareMemberSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareMemberAdded;
      props: ShareMemberAddedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareLinkGenerated;
      props: ShareLinkGeneratedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareLinkCopied;
      props: ShareLinkCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareSaved;
      props: ShareSavedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareAccessChanged;
      props: ShareAccessChangedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareAccessRevoked;
      props: ShareAccessRevokedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareEditRequested;
      props: ShareEditRequestedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareNotificationReceived;
      props: ShareNotificationReceivedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareNotificationClicked;
      props: ShareNotificationClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalConfirmed;
      props: ShareExternalConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalEmailAdded;
      props: ShareExternalEmailAddedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalEmailRemoved;
      props: ShareExternalEmailRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalInlineOpened;
      props: ShareExternalInlineOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalInlineCancelled;
      props: ShareExternalInlineCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalRevealOpened;
      props: ShareExternalRevealOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalLinkSendSubmitted;
      props: ShareExternalLinkSendSubmittedEventProps;
    }

  // Unauthenticated Session Link Share
  | {
      event_name: TrackingEventName.SessionLinkShareOtpVerified;
      props: SessionLinkShareOtpVerifiedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionLinkShareOtpFailed;
      props: SessionLinkShareOtpFailedEventProps;
    }

  // Entity Verification
  | {
      event_name: TrackingEventName.VerificationStarted;
      props: VerificationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationCompleted;
      props: VerificationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationFailed;
      props: VerificationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationSuggestedFixApplied;
      props: VerificationSuggestedFixAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationFeedbackSubmitted;
      props: VerificationFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationOverallFeedbackSubmitted;
      props: VerificationOverallFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoTriggered;
      props: VerificationAutoTriggeredEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoPopoverShown;
      props: VerificationAutoPopoverShownEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoPopoverAction;
      props: VerificationAutoPopoverActionEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoModeToggled;
      props: VerificationAutoModeToggledEventProps;
    }

  // Evidence Prompts Bar (GRO-2764)
  | {
      event_name: TrackingEventName.EvidencePromptsBarShown;
      props: EvidencePromptsBarShownEventProps;
    }
  | {
      event_name: TrackingEventName.EvidencePromptsBarDismissed;
      props: EvidencePromptsBarDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidencePromptsBarExpanded;
      props: EvidencePromptsBarExpandedEventProps;
    }

  // Evidence Ask Heidi shortcuts + Sources picker
  | {
      event_name: TrackingEventName.EvidenceShortcutsOpened;
      props: EvidenceShortcutsOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShortcutRun;
      props: EvidenceShortcutRunEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShortcutSaveToggled;
      props: EvidenceShortcutSaveToggledEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShortcutSearchUsed;
      props: EvidenceShortcutSearchUsedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSourcesOpened;
      props: EvidenceSourcesOpenedEventProps;
    }

  // Evidence Summary Suggestion (GRO-2767)
  | {
      event_name: TrackingEventName.EvidenceSummarySuggestionShown;
      props: EvidenceSummarySuggestionShownEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSummarySuggestionDismissed;
      props: EvidenceSummarySuggestionDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSummarySuggestionGenerated;
      props: EvidenceSummarySuggestionGeneratedEventProps;
    }

  // Document suggestion banner
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerShown;
      props: DocumentSuggestionBannerShownEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerDismissed;
      props: DocumentSuggestionBannerDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerCtaClicked;
      props: DocumentSuggestionBannerCtaClickedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerCarouselNavigated;
      props: DocumentSuggestionBannerCarouselNavigatedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerSuppressed;
      props: DocumentSuggestionBannerSuppressedEventProps;
    }

  // Source Recommendation Banner
  | {
      event_name: TrackingEventName.SourceRecommendationShown;
      props: SourceRecommendationShownEventProps;
    }
  | {
      event_name: TrackingEventName.SourceRecommendationDismissed;
      props: SourceRecommendationDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.SourceRecommendationApplied;
      props: SourceRecommendationAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.SourceRecommendationCustomised;
      props: SourceRecommendationCustomisedEventProps;
    }

  // Comms (Calls + Inbox)
  | {
      event_name: TrackingEventName.CommsDashboardTabViewed;
      props: CommsDashboardTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsEntryPointClicked;
      props: CommsEntryPointClickedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupStarted;
      props: CommsSetupStartedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupStepCompleted;
      props: CommsSetupStepCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupStepAbandoned;
      props: CommsSetupStepAbandonedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupCompleted;
      props: CommsSetupCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveSelected;
      props: ObjectiveSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveRemoved;
      props: ObjectiveRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveConfigured;
      props: ObjectiveConfiguredEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveViewed;
      props: ObjectiveViewedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxSessionStarted;
      props: InboxSessionStartedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxSessionEnded;
      props: InboxSessionEndedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxItemViewed;
      props: InboxItemViewedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxItemUpdated;
      props: InboxItemUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxFilterApplied;
      props: InboxFilterAppliedEventProps;
    }

  // Comms — KB Learning (Objectives tab)
  | {
      event_name: TrackingEventName.KbObjectivesTabViewed;
      props: KbObjectivesTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.KbScanButtonClicked;
      props: KbScanButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.KbPreviewButtonClicked;
      props: KbPreviewButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.KbApplyButtonClicked;
      props: KbApplyButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.KbApplySuccessViewed;
      props: KbApplySuccessViewedEventProps;
    }

  // Letter Sender
  | {
      event_name: TrackingEventName.LetterSenderModalOpened;
      props: LetterSenderModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderModalClosed;
      props: LetterSenderModalClosedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterRecipientAdded;
      props: LetterSenderRecipientAddedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterRecipientRemoved;
      props: LetterSenderRecipientRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterRecipientSearched;
      props: LetterSenderProviderSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderCcToggled;
      props: LetterSenderCcToggledEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderFileUploaded;
      props: LetterSenderFileUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderFileRemoved;
      props: LetterSenderFileRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderSent;
      props: LetterSenderSentEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderSendFailed;
      props: LetterSenderSendFailedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderFilePreviewed;
      props: LetterSenderFilePreviewedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderAttachmentRejected;
      props: LetterSenderAttachmentRejectedEventProps;
    }
  // Session Merge
  | {
      event_name: TrackingEventName.SessionMergeReviewViewed;
      props: SessionMergeReviewViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeConfirmed;
      props: SessionMergeConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeIgnored;
      props: SessionMergeIgnoredEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeReviewDismissed;
      props: SessionMergeReviewDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeFailed;
      props: SessionMergeFailedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeTabViewed;
      props: SessionMergeTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinked;
      props: SessionMergeUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeRelinked;
      props: SessionMergeRelinkedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeDissolvePromptViewed;
      props: SessionMergeDissolvePromptViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeDissolveConfirmed;
      props: SessionMergeDissolveConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeDissolveCancelled;
      props: SessionMergeDissolveCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinkPromptViewed;
      props: SessionMergeUnlinkPromptViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinkConfirmed;
      props: SessionMergeUnlinkConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinkCancelled;
      props: SessionMergeUnlinkCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeOnboardingViewed;
      props: SessionMergeOnboardingViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeOnboardingAccepted;
      props: SessionMergeOnboardingAcceptedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeOnboardingDismissed;
      props: SessionMergeOnboardingDismissedEventProps;
    };

declare module '@heidi/analytics/events' {
  interface TrackingEventRegistry {
    event: TrackingEventV2;
  }
}
